import { Injectable } from '@angular/core';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { UserAccount } from '@rogers/self-serve-common';
import {
  AccountDetail,
  AccountDetailFacadeService,
  AccountListFacadeService,
  AccountSubscription,
} from '@rogers/self-serve-common/account';
import { BillingDetailFacadeService } from '@rogers/self-serve-common/billing-detail';
import { getLatestValue } from '@rogers/self-serve-utils';
import { get } from 'lodash';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AccountDetailService {
  constructor(
    private readonly accountDetailFacadeService: AccountDetailFacadeService,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly billingDetailFacadeService: BillingDetailFacadeService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
  ) {}

  selectedAccount$ = this.userInformationStateFacade.selectedAccountNumber$.pipe(
    switchMap(accountNumber => this.accountListFacadeService.getAccount$(accountNumber)),
    filter(account => !!account),
  );

  distinctSelectedAccount$ = this.selectedAccount$.pipe(
    distinctUntilChanged(
      (selectedAccount, previousSelectedAccount) =>
        selectedAccount.accountNumber === previousSelectedAccount.accountNumber,
    ),
  );

  get showWirelessProductNotification$() {
    return this.accountListFacadeService.accountList$.pipe(
      map(accounts => !accounts.some(account => account.isWirelessOrConsolidatedAccount || account.isPrepaidAccount)),
    );
  }

  get selectedAccountType$() {
    return this.userInformationStateFacade.getSelectedAccountNumber().pipe(
      switchMap(accountNumber => this.getAccountDetail$(accountNumber)),
      map(account => account.accountType || ''),
    );
  }

  get selectedAccountDetail(): AccountDetail {
    return getLatestValue<AccountDetail>(this.getSelectedAccountDetail$());
  }

  get firstAccountProvince$() {
    return this.accountListFacadeService.accountList$.pipe(
      map(accounts => {
        const filteredAccounts = accounts?.filter(account => !account.isTentative);
        return filteredAccounts ? get(filteredAccounts, '[0].accountNumber') : '';
      }),
      filter(accountNumber => !!accountNumber),
      switchMap(accountNumber =>
        this.getAccountDetail$(accountNumber).pipe(
          map(accountDetails => get(accountDetails, 'billingAddress.province', '')),
        ),
      ),
      filter(province => !!province),
      distinctUntilChanged(),
    );
  }

  getAccountSubscriptions$(): Observable<AccountSubscription[]> {
    return this.accountDetailFacadeService.getAccountDetails$().pipe(
      filter(accountDetails => accountDetails.some(account => account.loaded)),
      map(accountDetails => {
        return accountDetails
          .filter(accountDetail => accountDetail.loaded)
          .map(accountDetail => accountDetail.subscriptions)
          .reduce((acc, val) => acc.concat(val), []);
      }),
    );
  }

  getSelectedAccountDetail$(): Observable<AccountDetail> {
    return this.userInformationStateFacade
      .getSelectedAccountNumber()
      .pipe(mergeMap(selectedAccountNumber => this.getAccountDetail$(selectedAccountNumber)));
  }

  getAccountDetail$(accountNumber) {
    return this.accountDetailFacadeService
      .getAccountDetail$(accountNumber)
      .pipe(filter(accountDetail => accountDetail.loaded || accountDetail.hasError));
  }

  getAccountDetails$() {
    return this.accountDetailFacadeService
      .getAccountDetails$()
      .pipe(
        filter(
          accountDetails =>
            !!accountDetails &&
            accountDetails.length > 0 &&
            accountDetails.some(accountDetail => accountDetail.loaded || accountDetail.hasError),
        ),
      );
  }

  fetchAccountsDetails$() {
    return this.accountListFacadeService.accountList$.pipe(
      take(1),
      withLatestFrom(this.accountDetailFacadeService.getAllLoadedAccountNumbers$()),
      tap(([accounts, loadedAccounts]) => {
        accounts
          .filter(account => !account.isTentative)
          .forEach(account => {
            if (!loadedAccounts.find(loadedAccount => account.accountNumber === loadedAccount)) {
              this.fetchAccountDetail(account);
              this.fetchBillingDetail(account);
            }
          });
      }),
    );
  }

  fetchSelectedAccountDetail$() {
    return this.accountListFacadeService.isAccountListLoaded$.pipe(
      filter(isLoaded => isLoaded),
      switchMap(_ => this.userInformationStateFacade.getSelectedAccountNumber()),
      switchMap(selectedAccountNumber => this.accountListFacadeService.getAccount$(selectedAccountNumber)),
      withLatestFrom(this.accountDetailFacadeService.getAllLoadedAccountNumbers$()),
      tap(([selectedAccount, loadedAccounts]) => {
        this.loadAccountDetail(selectedAccount, loadedAccounts);
      }),
    );
  }

  fetchAccountDetail$(accountNumber, _accountAlias?, _accountTypeNumber?): Observable<boolean> {
    return this.accountDetailFacadeService.getAccountDetail$(accountNumber).pipe(
      switchMap(account => {
        if (!!account && (account.loaded || account.loading || account.hasError)) {
          return of(true);
        }
        return this.accountListFacadeService.getAccount$(accountNumber).pipe(
          map(account => {
            this.fetchAccountDetail(account);
            this.fetchBillingDetail(account);

            return false;
          }),
        );
      }),
    );
  }

  getBillingDetail$(accountNumber) {
    return this.billingDetailFacadeService.getBillingDetail$(accountNumber);
  }

  getBillingDetails$() {
    return this.billingDetailFacadeService.getBillingDetails$();
  }

  private fetchAccountDetail(account: UserAccount) {
    this.accountDetailFacadeService.fetchAccountDetail(account.accountNumber, account.alias, account.accountTypeNumber);
  }

  private fetchBillingDetail(account: UserAccount) {
    this.billingDetailFacadeService.loadBillingDetail(
      account.accountNumber,
      account.alias,
      account.accountTypeNumber,
      account.isPrepaidAccount,
      false,
    );
  }

  loadBillingDetail$ = () => {
    return this.distinctSelectedAccount$.pipe(
      switchMap(account =>
        this.billingDetailFacadeService.loadBillingDetail$(
          account.accountNumber,
          account.alias,
          account.accountTypeNumber,
          account.isPrepaidAccount,
        ),
      ),
    );
  };

  removeAccount = (accountNumber: string) => {
    this.accountListFacadeService.removeAccount(accountNumber);
  };

  private loadAccountDetail(selectedAccount, loadedAccounts) {
    if (
      !!selectedAccount &&
      !selectedAccount.isTentative &&
      !loadedAccounts.find(loadedAccount => selectedAccount.accountNumber === loadedAccount)
    ) {
      this.fetchAccountDetail(selectedAccount);
      if (!selectedAccount.isPrepaidAccount) {
        this.fetchBillingDetail(selectedAccount);
      }
    }
  }
}
